/**
 * @package FlorenceEGI — EGI — Home Supreme Creators Spread renderer
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 2.0.0
 * @date 2026-04-22
 * @purpose Island 3 — 3 spreads editoriali alternati. Foto sx/dx alternata,
 *          bio dai count API (no fabrication), signature italica delle iniziali,
 *          3 tile FEPlate decorativi, CTA portfolio. Consuma
 *          /api/home-supreme/featured-creators (prime 3).
 */

import { el, clear, lazyImg, buildSrcset, pickVariant } from "./utils";
import type { ImageVariants } from "./utils";
import { buildPlate, pickTone } from "./plate";

type WorkDto = {
    id: number;
    title: string;
    url: string;
    image: ImageVariants;
};

type CreatorDto = {
    id: number;
    name: string;
    nick_name: string | null;
    avatar: string | null;
    egis_count: number;
    collections_count: number;
    works?: WorkDto[];
    url: string;
};

function initials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return "";
    if (parts.length === 1) {
        const p = parts[0] ?? "";
        return p.charAt(0).toUpperCase() + ".";
    }
    const first = parts[0] ?? "";
    const last = parts[parts.length - 1] ?? "";
    return first.charAt(0).toUpperCase() + ". " + last;
}

function splitName(name: string): { main: string; italic: string } {
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return { main: name, italic: "" };
    return {
        main: parts.slice(0, -1).join(" "),
        italic: parts[parts.length - 1] ?? "",
    };
}

function buildBio(c: CreatorDto): string {
    const egi = c.egis_count;
    const coll = c.collections_count;
    if (egi === 0 && coll === 0) return "Bottega aperta. Prime opere in preparazione.";
    if (egi > 0 && coll > 0) return `${egi} opere in ${coll} ${coll === 1 ? "collezione" : "collezioni"}. Una firma che custodisce la maniera fiorentina contemporanea.`;
    if (egi > 0) return `${egi} opere in catalogo. Una voce che traversa il Rinascimento digitale.`;
    return `${coll} ${coll === 1 ? "collezione" : "collezioni"} in allestimento.`;
}

function spread(c: CreatorDto, index: number): HTMLElement {
    const reverse = index % 2 === 1;
    const alt = index === 1;
    const cls = [
        "fe-creator-spread",
        alt ? "fe-creator-spread--alt" : "",
        reverse ? "fe-creator-spread--reverse" : "",
    ].filter(Boolean).join(" ");
    const article = el("article", { className: cls });

    // PHOTO
    const photo = el("div", { className: "fe-creator-spread__photo" });
    if (c.avatar) {
        // Creator photo ~40vw desktop → desktop (800). Avatar è già un URL
        // statico (profile_photo_url) — non ha varianti. Risoluzione tipica OK.
        const img = lazyImg(c.avatar, c.name, {
            width: 800, height: 1000,
            sizes: "(min-width:900px) 40vw, 100vw",
        });
        img.className = "fe-creator-spread__img";
        photo.appendChild(img);
    } else {
        const plate = buildPlate(`creator-${c.id}`, alt ? "travertino" : "nero");
        plate.setAttribute("class", "fe-creator-spread__img");
        photo.appendChild(plate);
    }
    photo.appendChild(el("span", {
        className: "fe-creator-spread__caption",
        text: "Ritratto · " + (c.nick_name ?? c.name),
    }));
    article.appendChild(photo);

    // BODY
    const body = el("div", { className: "fe-creator-spread__body" });
    const kicker = el("div", { className: "fe-kicker fe-creator-spread__kicker" });
    kicker.append(
        el("span", { className: "fe-hair" }),
        document.createTextNode(`Creatore N.° ${String(index + 1).padStart(2, "0")}`),
    );
    body.appendChild(kicker);

    const { main, italic } = splitName(c.name);
    const name = el("h3", { className: "fe-creator-spread__name" });
    name.appendChild(document.createTextNode(main));
    if (italic) {
        name.appendChild(document.createTextNode(" "));
        name.appendChild(el("em", { className: "fe-italic", text: italic }));
    }
    body.appendChild(name);

    body.appendChild(el("p", {
        className: "fe-creator-spread__bio",
        text: buildBio(c),
    }));

    body.appendChild(el("div", {
        className: "fe-creator-spread__signature",
        text: "— " + initials(c.name),
    }));

    // REAL WORKS (3 EGI reali del creator dal DTO; FEPlate solo se slot vuoto)
    const works = el("div", { className: "fe-creator-spread__works", attrs: { role: "list" } });
    for (let i = 0; i < 3; i++) {
        const w = c.works?.[i];
        const seed = `creator-${c.id}-work-${i}`;
        if (w) {
            const link = el("a", {
                className: "fe-creator-spread__work",
                attrs: { href: w.url, "aria-label": w.title, role: "listitem" },
            });
            const bestSrc = pickVariant(w.image, 400) ?? w.image?.src ?? "";
            const srcset = buildSrcset(w.image);
            const img = lazyImg(bestSrc, w.title, {
                width: 400, height: 400,
                sizes: "(min-width:900px) 14vw, 30vw",
                ...(srcset ? { srcset } : {}),
            });
            link.appendChild(img);
            works.appendChild(link);
        } else {
            const tile = el("div", {
                className: "fe-creator-spread__work",
                attrs: { role: "listitem", "aria-hidden": "true" },
            });
            const plate = buildPlate(seed, alt ? "travertino" : pickTone(seed));
            tile.appendChild(plate);
            works.appendChild(tile);
        }
    }
    body.appendChild(works);

    // CTA
    const cta = el("a", {
        className: "fe-creator-spread__cta",
        attrs: { href: c.url, "aria-label": `Portfolio di ${c.name}` },
    });
    cta.appendChild(document.createTextNode(`Scopri il portfolio — ${c.egis_count} opere`));
    const arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    arrow.setAttribute("width", "14");
    arrow.setAttribute("height", "10");
    arrow.setAttribute("viewBox", "0 0 14 10");
    arrow.setAttribute("fill", "none");
    arrow.setAttribute("aria-hidden", "true");
    const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", "M1 5h12M9 1l4 4-4 4");
    p.setAttribute("stroke", "currentColor");
    p.setAttribute("stroke-width", "1");
    p.setAttribute("stroke-linecap", "round");
    p.setAttribute("stroke-linejoin", "round");
    arrow.appendChild(p);
    cta.appendChild(arrow);
    body.appendChild(cta);

    article.appendChild(body);
    return article;
}

export function renderCreatorsSpread(host: HTMLElement, data: Record<string, unknown>): void {
    clear(host);
    const items = Array.isArray((data as { items?: CreatorDto[] }).items)
        ? (data as { items: CreatorDto[] }).items.slice(0, 3)
        : [];
    if (!items.length) {
        host.appendChild(el("p", { className: "empty", text: "—" }));
        return;
    }
    const wrap = el("div", { className: "fe-creators", attrs: { role: "list" } });
    items.forEach((c, i) => {
        const s = spread(c, i);
        s.setAttribute("role", "listitem");
        wrap.appendChild(s);
    });
    host.appendChild(wrap);
}
